import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
import {
  StyledTableGap,
  StyledAddButton,
  StyledTableCell,
  StyledTableRow,
} from '../../Table.styles';
import { useTranslation } from 'react-i18next';
import { IColumn } from '../../Table';
import { StyledNoticeStack } from '../../../../pages/ActionPlan/ActionPlan.styles';
import { DueDatePicker } from '../../../../pages/ActionPlan/components/DueDatePicker';
import dayjs from 'dayjs';
import { Priority } from '../../../../types/Initiative';
import { CustomResource, ResourceCategory } from '../../../../types/CustomResource';
import { PLAN_TYPE, RESOURCE_TYPES_BY_TAB_ID } from '../../../../pages/ActionPlan/constants';
import { useRowsValidation } from './useRowsValidation';
import {
  CustomResourcesApi,
  LOCAL_RESOURCE_ID_PREFIX,
} from '../../../../pages/ActionPlan/hooks/useCustomResources';
import { InitiativesStatusApi } from '../../../../pages/ActionPlan/hooks/useInitiativesStatus';

export type CustomResourcesProps = {
  category: ResourceCategory;
  planTypeId: keyof typeof PLAN_TYPE;
  columns: IColumn[];
  isPreviewMode: boolean;
  customResourcesApi: CustomResourcesApi;
  onCheckboxClick: (id: string, tabId: string) => void;
  initiativesStatusData?: {
    initiativesStatusApi: InitiativesStatusApi;
  };
  selectedItems: string[];
};

export const CustomResources = ({
  category,
  planTypeId,
  columns,
  customResourcesApi,
  isPreviewMode,
  onCheckboxClick,
  initiativesStatusData,
  selectedItems,
}: CustomResourcesProps) => {
  const { t } = useTranslation();
  const {
    customResources,
    createNewResource,
    updateResourceFieldByUuid,
    getResourcesByPlanTypeAndCategory,
    getPersistentResources,
    getTransientResources,
  } = customResourcesApi;

  const { rowsErrors, validateRow } = useRowsValidation();

  if (!customResources) {
    return null;
  }

  const customResourcesByPlanTypeAndCategory = getResourcesByPlanTypeAndCategory({
    resources: customResources,
    actionPlanType: PLAN_TYPE[planTypeId].field,
    category: category,
  });
  const persistentResources = getPersistentResources({
    resources: customResourcesByPlanTypeAndCategory,
  });
  const transientResources = getTransientResources({
    resources: customResourcesByPlanTypeAndCategory,
  });

  const persistentResourcesRows = persistentResources.map((row) => {
    const isChecked = !!(
      row.isChecked ??
      selectedItems?.find((item) => {
        if (!item || !row.crUuid) return false;

        return (
          item?.replace(LOCAL_RESOURCE_ID_PREFIX, '') ===
          row.crUuid?.replace(LOCAL_RESOURCE_ID_PREFIX, '')
        );
      })
    );

    return (
      <StyledTableRow key={row.crUuid}>
        {columns.map((col) => {
          const field = col.id;

          switch (field) {
            case 'initiativeType':
              return (
                <StyledTableCell key={col.id}>
                  <Typography>
                    {
                      RESOURCE_TYPES_BY_TAB_ID[row.actionPlanType].find(
                        (option) => option.id === row.crType,
                      )?.label
                    }
                  </Typography>
                </StyledTableCell>
              );

            case 'title':
              return (
                <StyledTableCell key={col.id}>
                  <Typography>{row.crTitle}</Typography>
                </StyledTableCell>
              );

            case 'priority':
              return (
                <StyledTableCell key={col.id}>
                  <Typography>
                    {Priority[row.crPriority as unknown as keyof typeof Priority]}
                  </Typography>
                </StyledTableCell>
              );

            case 'initiativeDueDate':
              return (
                <StyledTableCell key={col.id}>
                  <DueDatePicker
                    date={row.crDueDate ? dayjs(row.crDueDate) : null}
                    setDate={(date) =>
                      updateResourceFieldByUuid({
                        uuid: row.crUuid,
                        field: 'crDueDate',
                        value: date.format('YYYY-MM-DD'),
                      })
                    }
                    disabled={isPreviewMode || !isChecked}
                  />
                </StyledTableCell>
              );

            case 'initiativeNote':
              return (
                <StyledTableCell key={col.id}>
                  <StyledNoticeStack spacing={2}>
                    <TextField
                      defaultValue={row.crNote ?? ''}
                      placeholder={`${t('notes')}...`}
                      variant="standard"
                      onBlur={(e) =>
                        updateResourceFieldByUuid({
                          uuid: row.crUuid,
                          field: 'crNote',
                          value: e.target.value,
                        })
                      }
                      disabled={isPreviewMode || !isChecked}
                    />
                    {!isPreviewMode && (
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          updateResourceFieldByUuid({
                            uuid: row.crUuid,
                            field: 'isChecked',
                            value: e.target.checked,
                          });
                          onCheckboxClick(row.crUuid, planTypeId);
                        }}
                      />
                    )}
                  </StyledNoticeStack>
                </StyledTableCell>
              );
            case 'actioned':
              return (
                <StyledTableCell key={col.id}>
                  <Checkbox
                    checked={initiativesStatusData.initiativesStatusApi.getActionedItemValueById(
                      row.crUuid,
                    )}
                    onChange={() => {
                      initiativesStatusData.initiativesStatusApi.updateActionedItemById(row.crUuid);
                    }}
                    id={row.crUuid}
                  />
                </StyledTableCell>
              );
            default:
              return null;
          }
        })}
      </StyledTableRow>
    );
  });

  const transientResourcesRows = transientResources.map((row) => {
    const errors = rowsErrors[row.crUuid] ?? {};
    const isChecked = !!(
      row.isChecked ??
      selectedItems?.find((item) => {
        if (!item || !row.crUuid) return false;

        return (
          item?.replace(LOCAL_RESOURCE_ID_PREFIX, '') ===
          row.crUuid?.replace(LOCAL_RESOURCE_ID_PREFIX, '')
        );
      })
    );

    return (
      <StyledTableRow key={row.crUuid}>
        {columns.map((col) => {
          const field = col.id;

          switch (field) {
            case 'initiativeType':
              return (
                <StyledTableCell key={col.id}>
                  <FormControl variant="standard" error={!!errors.crType} fullWidth>
                    <Select
                      value={row.crType ?? ''}
                      variant="standard"
                      onChange={(e) => {
                        const newValue = e.target.value as CustomResource['crType'];
                        updateResourceFieldByUuid({
                          uuid: row.crUuid,
                          field: 'crType',
                          value: newValue,
                        });

                        const isValid = validateRow({ ...row, crType: newValue });
                        if (isChecked && !isValid) {
                          updateResourceFieldByUuid({
                            uuid: row.crUuid,
                            field: 'isChecked',
                            value: false,
                          });
                          onCheckboxClick(row.crUuid, planTypeId);
                        }
                      }}
                    >
                      {RESOURCE_TYPES_BY_TAB_ID[row.actionPlanType].map((typeOption) => (
                        <MenuItem key={typeOption.id} value={typeOption.id}>
                          {typeOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.crType && <FormHelperText error>{t('requiredField')}</FormHelperText>}
                  </FormControl>
                </StyledTableCell>
              );

            case 'title':
              return (
                <StyledTableCell key={col.id}>
                  <TextField
                    defaultValue={row.crTitle ?? ''}
                    variant="standard"
                    error={!!errors.crTitle}
                    helperText={errors.crTitle ? t('requiredField') : ''}
                    onBlur={(e) => {
                      const newValue = e.target.value;

                      updateResourceFieldByUuid({
                        uuid: row.crUuid,
                        field: 'crTitle',
                        value: newValue,
                      });

                      const isValid = validateRow({ ...row, crTitle: newValue });
                      if (isChecked && !isValid) {
                        updateResourceFieldByUuid({
                          uuid: row.crUuid,
                          field: 'isChecked',
                          value: false,
                        });
                        onCheckboxClick(row.crUuid, planTypeId);
                      }
                    }}
                  />
                </StyledTableCell>
              );

            case 'priority':
              return (
                <StyledTableCell key={col.id}>
                  <FormControl variant="standard" fullWidth>
                    <Select
                      value={row.crPriority ?? ''}
                      variant="standard"
                      onChange={(e) => {
                        const newValue = e.target.value as Priority;
                        updateResourceFieldByUuid({
                          uuid: row.crUuid,
                          field: 'crPriority',
                          value: newValue,
                        });
                      }}
                    >
                      {Object.keys(Priority).map((key) => (
                        <MenuItem key={key} value={key}>
                          {Priority[key as keyof typeof Priority]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </StyledTableCell>
              );

            case 'initiativeDueDate':
              return (
                <StyledTableCell key={col.id}>
                  <DueDatePicker
                    date={row.crDueDate ? dayjs(row.crDueDate) : null}
                    setDate={(date) =>
                      updateResourceFieldByUuid({
                        uuid: row.crUuid,
                        field: 'crDueDate',
                        value: date.format('YYYY-MM-DD'),
                      })
                    }
                    disabled={false}
                  />
                </StyledTableCell>
              );

            case 'initiativeNote':
              return (
                <StyledTableCell key={col.id}>
                  <StyledNoticeStack spacing={2}>
                    <TextField
                      defaultValue={row.crNote ?? ''}
                      placeholder={`${t('notes')}...`}
                      variant="standard"
                      onBlur={(e) =>
                        updateResourceFieldByUuid({
                          uuid: row.crUuid,
                          field: 'crNote',
                          value: e.target.value,
                        })
                      }
                    />
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const isValid = validateRow(row);

                        if (checked) {
                          if (!isValid) {
                            return;
                          }

                          updateResourceFieldByUuid({
                            uuid: row.crUuid,
                            field: 'isChecked',
                            value: true,
                          });
                          onCheckboxClick(row.crUuid, planTypeId);
                        } else {
                          updateResourceFieldByUuid({
                            uuid: row.crUuid,
                            field: 'isChecked',
                            value: false,
                          });
                          onCheckboxClick(row.crUuid, planTypeId);
                        }
                      }}
                    />
                  </StyledNoticeStack>
                </StyledTableCell>
              );
            default:
              return null;
          }
        })}
      </StyledTableRow>
    );
  });

  return (
    <>
      {persistentResourcesRows}
      {!isPreviewMode && (
        <>
          {transientResourcesRows}
          <StyledTableGap
            sx={{
              bgcolor: 'transparent',
              border: 0,
              '& td': { border: 0, p: 0, bgcolor: 'transparent' },
            }}
          >
            <TableCell colSpan={99}>
              <Box sx={{ height: 50 }}>
                <StyledAddButton
                  variant="outlined"
                  onClick={() =>
                    createNewResource({
                      actionPlanType: PLAN_TYPE[planTypeId].field,
                      category: category,
                    })
                  }
                >
                  {t('actionPlan.addCustomResource')}
                </StyledAddButton>
              </Box>
            </TableCell>
          </StyledTableGap>
          <StyledTableGap
            sx={{
              bgcolor: 'transparent',
              border: 0,
              '& td': { border: 0, p: 0, bgcolor: 'transparent' },
            }}
          >
            <TableCell colSpan={99}>
              <Box sx={{ height: 50 }} />
            </TableCell>
          </StyledTableGap>
        </>
      )}
    </>
  );
};
